import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Globe, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TZ_OPTIONS = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu', 'America/Puerto_Rico', 'UTC'];

// Company-wide timezone (single source of truth). Only company admins may change it.
const CompanyTimezoneCard = () => {
  const { user } = useAuth();
  const canEdit = user?.role === 'company_admin' || user?.role === 'super_admin';
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/payrolls/timezone`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => {});
  }, []);
  const save = async (tz) => {
    setSaving(true);
    try {
      await axios.put(`${API}/payrolls/timezone`, { timezone: tz }, { withCredentials: true });
      setData({ ...data, timezone: tz, explicit: tz });
      toast.success(`Company timezone set to ${tz}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save timezone'); } finally { setSaving(false); }
  };
  if (!data) return null;
  const options = TZ_OPTIONS.includes(data.timezone) ? TZ_OPTIONS : [data.timezone, ...TZ_OPTIONS];
  const now = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: data.timezone });
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 mb-6" data-testid="company-timezone-card" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}><Globe className="w-5 h-5 text-[#002FA7]" /> Company timezone</div>
        <select value={data.timezone} disabled={!canEdit || saving} onChange={(e) => save(e.target.value)} data-testid="company-timezone-select"
                className="px-3 py-1.5 text-sm border border-slate-300 rounded-md bg-white disabled:opacity-60">
          {options.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
        </select>
        {saving && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        <span className="text-xs text-slate-500" data-testid="company-timezone-now">Now {now} there{!data.explicit ? ' · default (not set yet)' : ''}</span>
      </div>
      <p className="text-xs text-slate-500 mt-2">Payroll weeks, profit recaps, appointment times and live-tracking ETAs all follow this timezone for everyone in your company. {canEdit ? '' : 'Only a company admin can change it.'}</p>
    </div>
  );
};

export default CompanyTimezoneCard;
